import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@config';

/**
 * useGcpAccelerators
 *
 * Fetches the GPU accelerator types that can actually be ATTACHED to N1 shapes
 * in a given GCP zone, via POST /api/instance-availability/accelerators (which
 * runs the live gcloud CLI). Availability is per-zone, and the list is the same
 * for every N1 shape in that zone, so the GCP form calls this once and shares
 * the result with the head node + every compute node group.
 *
 * Debounced + de-duplicated on (credentialId, zone) so switching shapes doesn't
 * re-hit the CLI. Fixed-GPU shapes (a2/g2/a3) don't need this — their GPU is
 * baked in and shown read-only from the catalog.
 *
 * @returns {{ acceleratorTypes: Array, loading: boolean, error: string|null,
 *             unableToVerify: boolean, zone: string|null }}
 */
export default function useGcpAccelerators({ provider = 'gcp', credentialId, region, zone, enabled = true }) {
    const [state, setState] = useState({
        acceleratorTypes: [], loading: false, error: null, unableToVerify: false, zone: null,
    });
    const debounceTimer = useRef(null);
    const lastKey = useRef(null);

    useEffect(() => {
        if (!enabled || !credentialId || !zone) return undefined;

        const key = JSON.stringify({ credentialId, zone });
        if (lastKey.current === key) return undefined;

        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(async () => {
            lastKey.current = key;
            setState((s) => ({ ...s, loading: true, error: null }));
            try {
                const resp = await axios.post(API_BASE_URL + '/api/instance-availability/accelerators', {
                    credentialId, provider, region, zone,
                });
                setState({
                    acceleratorTypes: resp.data?.acceleratorTypes || [],
                    loading: false,
                    error: resp.data?.error || null,
                    unableToVerify: resp.data?.unableToVerify || false,
                    zone,
                });
            } catch (err) {
                const msg = err.response?.data?.message || err.message;
                // Non-fatal: the form falls back to the static accelerator list.
                console.error('Failed to fetch GCP accelerators:', msg);
                setState({ acceleratorTypes: [], loading: false, error: msg, unableToVerify: true, zone });
            }
        }, 500);

        return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
    }, [enabled, credentialId, region, zone, provider]);

    return state;
}
